// The honesty model (threads §9.1, §9.2, §11, §12).
//
// The rule this file exists to enforce: **the UI must never be able to
// render a confident claim the protocol did not make.** Not "must not" as
// a code-review note — must not as a type error. So every claim here is a
// discriminated union whose weaker variants simply do not carry the field
// a stronger claim would need. There is no `arrivalMillis` on a stop the
// driver skipped, because a shape with an arrival time in it is a shape
// some caller eventually renders (§9.1). There is no position on a coarse
// run, because §11 says the operator decided this audience is not
// entitled to one, and a `null` in an otherwise-live shape is a hole a
// component fills with a placeholder dot.
//
// Nothing in here is a string the UI prints. Words are the app's job; the
// claims are the protocol's.

import {
  OUTCOME_NAME,
  STOP_OUTCOME,
  THREAD_MODE,
  THREAD_STATE,
  TRAVEL_MODE_NAME,
  type DegradationRow,
  type RawArrival,
  type RunPlan,
  type StopOutcomeName,
  type StopReasonCode,
  type SubscriberStatus,
  type ThreadUpdate,
  type TravelModeName
} from "./protocol.js";

/** §6 `THREAD_STALE`: past this, the interface must stop claiming "live". */
export const STALE_SECONDS = 90;

/**
 * Where the vehicle is, and why that is the answer.
 *
 * The four kinds are not four levels of the same thing. `withheld` is a
 * property of the *run* — the operator chose coarse and no amount of
 * waiting produces a dot — while `stale` and `unknown` are properties of
 * this device's connection. A UI that renders `withheld` as "waiting for
 * a position" has promised something that is never coming.
 */
export type PositionClaim =
  /** Fine mode, fresh: a snapped point on a road. Draw it. */
  | {
    kind: "live";
    segment: string;
    ratio: number;
    observedUnixSeconds: number;
    ageSeconds: number;
  }
  /** §11 coarse: the operator withheld position. Never draw a pin, not even a fuzzy one. */
  | { kind: "withheld"; lastStopIndex: number }
  /** §12: we had one, it is older than `THREAD_STALE`. Say so; do not draw it. */
  | { kind: "stale"; ageSeconds: number; lastStopIndex: number }
  /**
   * The run published a position and this device holds no routing map for
   * the area, so `(segment, ratio)` cannot be turned into a place. Not
   * stale — the observation is current — and not withheld — the operator
   * published it. There is simply no map here to put it on.
   */
  | { kind: "unplaceable"; segment: string; ageSeconds: number; lastStopIndex: number }
  /** Nothing has arrived yet, or the run has not started. */
  | { kind: "unknown" };

/**
 * Whether this device holds a routing map for the area at all (§9, §13).
 *
 * A route graph is regional and served from a host the operator
 * configures. Without one there is no `locate()` to place a position and
 * no `matrix()` to time a leg — and, because the thread's topic tag is
 * derived from the graph's own epoch (§4.2), no way to open the
 * capability either. That is a designed state, not an error, and it is
 * emphatically not the same claim as "static metric": the static metric
 * is a real routed answer computed without live traffic, and this is no
 * routed answer at all.
 */
export type MapClaim =
  | { kind: "graph"; profile: string | null; epoch: string }
  | { kind: "none"; reason: "not-configured" | "unreachable" | "wrong-epoch" };

/** §9.2 — which graph the number was computed on, and whether it matched the run. */
export interface ProfileClaim {
  basis: "matched" | "mismatched" | "unstated";
  /** The graph this device holds, when it named itself. */
  graph: string | null;
  /** How the run said it moves, when it said. */
  run: TravelModeName | null;
}

/**
 * When it gets here, and on what evidence.
 *
 * `estimate` and `scheduled` are the only two variants with a time in
 * them, and they are different claims: §12 — "bus expected 07:44" and
 * "bus is here, arriving 07:44" are not the same sentence, and the second
 * one is the reason anyone opened the link.
 */
export type ArrivalClaim =
  /** Nothing to say. The run is over, or this stop is not on it. */
  | { kind: "none" }
  /** Already served (§9.1). There is no arrival time for a stop behind the van. */
  | { kind: "served" }
  /**
   * §9.1: marked skipped or failed. The vehicle is not coming, and this
   * shape has no arrival time in it at all — not a stale one, not a large
   * one, none.
   */
  | {
    kind: "not-coming";
    outcome: Extract<StopOutcomeName, "skipped" | "failed">;
    reasonCode: StopReasonCode | null;
  }
  /** Computed here, on this device, from a broadcast position (§9). */
  | {
    kind: "estimate";
    arrivalMillis: number;
    secondsFromNow: number;
    stopsAway: number;
    /** §12 rows 1 and 2: live traffic, or the static metric. */
    metric: "live-traffic" | "static-metric";
    /** §11: routed from the reported position, or from the last stop it called at. */
    origin: "reported-position" | "last-stop";
    profile: ProfileClaim;
    /** The observation this was computed from is past `THREAD_STALE`. */
    stale: boolean;
    observationAgeSeconds: number;
  }
  /** §12 bottom rows: the published timetable, marked as a timetable. */
  | { kind: "scheduled"; arrivalMillis: number }
  /**
   * §20.2 — the estimate a recipient can make from their own stop alone.
   *
   * A plan never goes on the wire, so a customer holding a link cannot
   * know the doorsteps between the van and theirs. Routing straight from
   * the reported position to this door therefore ignores every delivery
   * in between, and those can only ever push the arrival **later**. So
   * this is a floor: "not before 21:34" is true, while "arriving 21:34"
   * would be a promise nothing here can keep.
   *
   * `stopsAhead` comes from the cumulative outcome map (§5.2) — how many
   * stops before this one the run has still not dealt with. Every
   * link-holder already has it and it names no address, which makes it
   * the honest way to say "you are third in line".
   */
  | {
    kind: "no-earlier-than";
    arrivalMillis: number;
    secondsFromNow: number;
    stopsAhead: number;
    metric: "live-traffic" | "static-metric";
    profile: ProfileClaim;
    stale: boolean;
    observationAgeSeconds: number;
  }
  /**
   * No routing map for this area, so no arrival time was computed — by
   * anyone, anywhere. Distinct from `none` (nothing to say), from a stale
   * estimate (an answer that has aged), and from `static-metric` (a real
   * routed answer without live traffic). The only honest sentence here is
   * that we cannot work it out on this device.
   */
  | { kind: "no-map"; reason: "not-configured" | "unreachable" | "wrong-epoch" };

/** Before / during / after — the three states the consumer app is built around. */
export type RunPhase = "before" | "during" | "after";

export interface RunStatus {
  /** §12's row, verbatim. Never upgrade it. */
  row: DegradationRow;
  /** The sentence the protocol authorises, verbatim from the subscriber. */
  claim: string;
  /** Whether this device holds a routing map for the area at all. */
  map: MapClaim;
  /** §11. `unknown` before the first record — not a synonym for coarse. */
  granularity: "fine" | "coarse" | "unknown";
  position: PositionClaim;
  /** Whether anything has been heard at all, and when. */
  observed: {
    ever: boolean;
    ageSeconds: number | null;
    stale: boolean;
    /** The newest record's own timestamp — §5.2 field 2, whole seconds. */
    atUnixSeconds: number | null;
  };
  state: number;
  phase: RunPhase;
  /** The last stop the run has dealt with, in plan order (§5.2). */
  stopIndex: number;
  /** Cumulative, one entry per plan stop (§5.2.1). */
  outcomes: number[];
  lastOutcome: ThreadUpdate["lastOutcome"];
  /**
   * §20.7.1 — the run's photo-chain head, re-stated and signed in every
   * record. This is the verifiable code a page can show beside "a photo
   * was taken"; the wire never carries a per-stop hash.
   */
  photoChain: string | null;
  travelMode: TravelModeName | null;
  /** The link has passed its `notAfter`; key material is gone (§5.4). */
  expired: boolean;
  /** True when this is an in-tab demonstration, never a real run. */
  demo: boolean;
}

function granularityOf(update: ThreadUpdate | null): RunStatus["granularity"] {
  if (!update) return "unknown";
  if (update.mode === THREAD_MODE.FINE) return "fine";
  if (update.mode === THREAD_MODE.COARSE) return "coarse";
  return "unknown";
}

/**
 * §12 + §11, as one claim about position.
 *
 * The order of the tests is the whole point. Coarse is checked *before*
 * staleness, because a coarse run's missing position is a decision rather
 * than a degradation, and telling a parent "the position is out of date"
 * about a run that will never publish one is a lie in the polite
 * direction.
 */
export function positionClaim(
  update: ThreadUpdate | null,
  options: {
    nowMillis?: number;
    staleSeconds?: number;
    map?: MapClaim;
    /**
     * The newest update that actually carried a position.
     *
     * Not every record on a fine run does. A **mark** carries none — the
     * publisher seals `markStop` with no match (§5.2.1) — and §11 lets a
     * publisher withhold position near first and last stops. Reading a
     * single positionless record as "this run is coarse" would redraw the
     * whole screen as a stops-only run the moment a driver taps
     * Delivered, which is both wrong and alarming. So the vehicle's last
     * known position stands until it ages out, and staleness is what
     * takes it down.
     */
    positioned?: ThreadUpdate | null;
  } = {}
): PositionClaim {
  const staleSeconds = options.staleSeconds ?? STALE_SECONDS;
  const nowMillis = options.nowMillis ?? Date.now();
  const map = options.map ?? { kind: "graph", profile: null, epoch: "" };
  if (!update) return { kind: "unknown" };
  // §11 coarse is a decision about the *run*, read off `mode`, and it is
  // checked first: telling a parent "the position is out of date" about a
  // run that will never publish one is a lie in the polite direction.
  if (update.mode === THREAD_MODE.COARSE) {
    return { kind: "withheld", lastStopIndex: update.stopIndex };
  }
  const fix = options.positioned !== undefined
    ? options.positioned
    : (update.segment ? update : null);
  if (!fix?.segment) return { kind: "unknown" };
  const ageSeconds = Math.max(0, Math.floor(nowMillis / 1000) - fix.unixSeconds);
  if (ageSeconds > staleSeconds) {
    return { kind: "stale", ageSeconds, lastStopIndex: update.stopIndex };
  }
  if (map.kind === "none") {
    // Published, current, and unplaceable. Saying "stale" here would be a
    // lie in the polite direction; saying "live" would be one in the
    // other, since there is nothing to draw it on.
    return { kind: "unplaceable", segment: fix.segment, ageSeconds, lastStopIndex: update.stopIndex };
  }
  return {
    kind: "live",
    segment: fix.segment,
    ratio: fix.ratio,
    observedUnixSeconds: fix.unixSeconds,
    ageSeconds
  };
}

/**
 * Which of the consumer app's three states this run is in.
 *
 * `after` is decided by the run being over *or* by this viewer's own stop
 * being resolved — a customer whose parcel was delivered at 14:32 is
 * looking at an "after" screen even though the van is still working its
 * way down the street, and a customer whose stop was skipped is too.
 */
export function runPhase(
  update: ThreadUpdate | null,
  options: { myStopIndex?: number | null } = {}
): RunPhase {
  const myStopIndex = options.myStopIndex ?? null;
  if (!update) return "before";
  if (update.state === THREAD_STATE.COMPLETED || update.state === THREAD_STATE.CANCELED) return "after";
  if (myStopIndex != null) {
    const mine = update.outcomes[myStopIndex - 1] ?? STOP_OUTCOME.PENDING;
    if (mine !== STOP_OUTCOME.PENDING) return "after";
  }
  if (update.state === THREAD_STATE.SCHEDULED) return "before";
  return "during";
}

/**
 * Folds a subscriber's `status()` and its newest update into one claim.
 *
 * `status.row`, `status.claim` and the outcome map are passed through
 * untouched — they are the protocol's answer and this package has no
 * business improving them.
 */
export function runStatus(input: {
  status: SubscriberStatus | null;
  update: ThreadUpdate | null;
  /** The newest update carrying a position; see `positionClaim`. */
  positioned?: ThreadUpdate | null;
  map?: MapClaim;
  expired?: boolean;
  demo?: boolean;
  myStopIndex?: number | null;
  nowMillis?: number;
  staleSeconds?: number;
}): RunStatus {
  const { status, update } = input;
  const map = input.map ?? { kind: "graph", profile: null, epoch: "" };
  const staleSeconds = input.staleSeconds ?? STALE_SECONDS;
  const nowMillis = input.nowMillis ?? Date.now();
  const ageSeconds = update ? Math.max(0, Math.floor(nowMillis / 1000) - update.unixSeconds) : null;
  return {
    row: status?.row ?? "neither",
    claim: status?.claim ?? "scheduled prediction only",
    map,
    granularity: granularityOf(update),
    position: positionClaim(update, {
      nowMillis,
      staleSeconds,
      map,
      positioned: input.positioned !== undefined ? input.positioned : (update?.segment ? update : null)
    }),
    observed: {
      ever: update != null,
      ageSeconds,
      stale: ageSeconds != null && ageSeconds > staleSeconds,
      atUnixSeconds: update?.unixSeconds ?? null
    },
    state: update?.state ?? THREAD_STATE.SCHEDULED,
    phase: runPhase(update, { myStopIndex: input.myStopIndex ?? null }),
    stopIndex: update?.stopIndex ?? 0,
    outcomes: update?.outcomes ?? [],
    lastOutcome: update?.lastOutcome ?? null,
    photoChain: update?.photoChain ?? null,
    travelMode: TRAVEL_MODE_NAME[update?.travelMode ?? 0] ?? null,
    expired: input.expired ?? false,
    demo: input.demo ?? false
  };
}

/**
 * Turns the engine's arrival estimate into a claim, honestly.
 *
 * This is not a formatting wrapper. It closes a real trap in the
 * convenience path: `follow.eta()` falls back to `scheduledArrival()`
 * whenever `estimateArrival()` returns null — and `estimateArrival()`
 * returns null for a stop that has **already been delivered** (§9.1). Fed
 * straight to a screen, that combination shows a customer holding a
 * delivered parcel a timetable entry for its arrival. So the outcome map
 * is consulted here first, before any estimate is allowed to speak.
 */
export function arrivalClaim(input: {
  raw: RawArrival | null;
  outcomes: number[];
  myStopIndex: number | null;
  /** Without a routing map there is no routed answer to give at all. */
  map?: MapClaim;
  /** The last stop the run dealt with, for the "already passed" test. */
  stopIndex?: number;
  state?: number;
}): ArrivalClaim {
  const { raw, outcomes, myStopIndex } = input;
  const map = input.map ?? { kind: "graph", profile: null, epoch: "" };
  if (myStopIndex != null) {
    const mine = outcomes[myStopIndex - 1] ?? STOP_OUTCOME.PENDING;
    if (mine === STOP_OUTCOME.DELIVERED) return { kind: "served" };
    if (mine === STOP_OUTCOME.SKIPPED || mine === STOP_OUTCOME.FAILED) {
      return {
        kind: "not-coming",
        outcome: OUTCOME_NAME[mine] as "skipped" | "failed",
        reasonCode: reasonForMyStop(raw)
      };
    }
  }
  // Outcomes sit **outside** §12's table and are checked before this:
  // "delivered at 14:32" survives having no map, no traffic and no
  // position. An arrival time does not.
  if (map.kind === "none") return { kind: "no-map", reason: map.reason };
  if (!raw) return { kind: "none" };
  // Defensive: the engine says `marked` for the same case. If the two
  // ever disagree, the one that refuses to state a time wins.
  if (raw.basis === "marked") {
    const outcome = raw.outcome === STOP_OUTCOME.FAILED ? "failed" : "skipped";
    return { kind: "not-coming", outcome, reasonCode: (raw.reasonCode ?? null) as StopReasonCode | null };
  }
  if (raw.arrivalMillis == null) return { kind: "none" };
  if (raw.basis === "schedule") return { kind: "scheduled", arrivalMillis: raw.arrivalMillis };
  return {
    kind: "estimate",
    arrivalMillis: raw.arrivalMillis,
    secondsFromNow: Math.max(0, raw.secondsFromNow ?? 0),
    stopsAway: raw.stopsAway ?? 0,
    metric: raw.basis,
    origin: raw.positionBasis === "reported-position" ? "reported-position" : "last-stop",
    profile: {
      basis: raw.profileBasis ?? "unstated",
      graph: raw.profile ?? null,
      run: raw.travelModeName ?? null
    },
    stale: raw.stale === true,
    observationAgeSeconds: raw.observationAgeSeconds ?? 0
  };
}

/**
 * The floor a recipient can compute from their own stop (§20.2).
 *
 * Deliberately routed through the *same* outcome guards as `arrivalClaim`
 * — served, skipped and failed all outrank an arrival time, and a floor
 * is still an arrival time. A stop already delivered must never show a
 * countdown because the customer's copy of the link happened to carry a
 * coordinate.
 */
export function floorClaim(input: {
  /** Seconds from the reported position to this door, ignoring stops between. */
  seconds: number | null;
  outcomes: number[];
  myStopIndex: number;
  map?: MapClaim;
  metric: "live-traffic" | "static-metric";
  profile: ProfileClaim;
  stale: boolean;
  observationAgeSeconds: number;
  nowMillis: number;
}): ArrivalClaim {
  const { outcomes, myStopIndex } = input;
  const mine = outcomes[myStopIndex - 1] ?? STOP_OUTCOME.PENDING;
  if (mine === STOP_OUTCOME.DELIVERED) return { kind: "served" };
  if (mine === STOP_OUTCOME.SKIPPED || mine === STOP_OUTCOME.FAILED) {
    return { kind: "not-coming", outcome: OUTCOME_NAME[mine] as "skipped" | "failed", reasonCode: null };
  }
  const map = input.map ?? { kind: "graph", profile: null, epoch: "" };
  if (map.kind === "none") return { kind: "no-map", reason: map.reason };
  if (input.seconds == null || !Number.isFinite(input.seconds)) return { kind: "none" };
  const seconds = Math.max(0, Math.round(input.seconds));
  // Everything before mine that nobody has resolved yet. Counted from the
  // map rather than from stopIndex, so a driver who marked stop 7 while
  // standing at stop 3 does not make four customers disappear from the
  // queue they are still in.
  let stopsAhead = 0;
  for (let index = 0; index < myStopIndex - 1; index++) {
    if ((outcomes[index] ?? STOP_OUTCOME.PENDING) === STOP_OUTCOME.PENDING) stopsAhead += 1;
  }
  return {
    kind: "no-earlier-than",
    arrivalMillis: input.nowMillis + seconds * 1000,
    secondsFromNow: seconds,
    stopsAhead,
    metric: input.metric,
    profile: input.profile,
    stale: input.stale,
    observationAgeSeconds: input.observationAgeSeconds
  };
}

/**
 * The wire carries one reason, for the most recent mark. When that mark
 * was some other stop, the reason for *this* stop is genuinely unknown
 * rather than inferrable, and null is the honest answer (§9.1) — which is
 * exactly what `estimateArrival` already decided, so this only unwraps it.
 */
function reasonForMyStop(raw: RawArrival | null): StopReasonCode | null {
  if (!raw || raw.basis !== "marked") return null;
  return (raw.reasonCode ?? null) as StopReasonCode | null;
}

/** The outcome asserted for one stop, or `pending` when nobody has said. */
export function outcomeOf(outcomes: number[], stopIndex: number): StopOutcomeName {
  return OUTCOME_NAME[outcomes[stopIndex - 1] ?? STOP_OUTCOME.PENDING] ?? "pending";
}

/**
 * How many stops of a plan are still pending, for a stop counter that is
 * true on a coarse run — the one number a school-transport parent gets.
 */
export function stopProgress(status: RunStatus, plan: RunPlan | null): {
  dealtWith: number;
  total: number;
  delivered: number;
  skipped: number;
  failed: number;
} {
  const total = plan?.stops.length ?? status.outcomes.length;
  let delivered = 0;
  let skipped = 0;
  let failed = 0;
  for (const outcome of status.outcomes) {
    if (outcome === STOP_OUTCOME.DELIVERED) delivered++;
    else if (outcome === STOP_OUTCOME.SKIPPED) skipped++;
    else if (outcome === STOP_OUTCOME.FAILED) failed++;
  }
  return { dealtWith: status.stopIndex, total, delivered, skipped, failed };
}
